import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { IonicVue } from '@ionic/vue';
import { i18n } from './i18n';
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query';
import './scss/app.scss';
import IonicCmpPlugin from '@/plugins/ionic-cmp-plugin';
import BaseCmpPlugin from '@/plugins/base-cmp-plugin';
import { DisableSwipeBackDirective } from './plugins/disable-swipe-back';
import logger from './services/logger';
import { logAppOpen } from './services/analytics';
import { initMetaAppEvents } from './services/meta-app-events';
import { SplashScreen } from '@capacitor/splash-screen';
import { startSplashFailsafe, clearSplashFailsafe } from './services/splash-failsafe';

// Do not call SplashScreen.show() here. The native launch splash is already
// shown via capacitor SplashScreen.launchAutoHide=false, and App.vue hides it.
// Calling show() on every JS boot re-flashes "Share Your Moves" after mid-session
// webview reloads (common after workout video memory pressure).

// Failsafe: never leave users on the native splash if the app shell fails to mount.
// App.vue now hides the splash as soon as it mounts, without waiting on Live Update.
startSplashFailsafe();

// Defer Datadog RUM so it does not compete with first paint / splash hide.
void import('./services/rum-logger');

// LiveUpdate.ready() moved to App.vue onMounted to ensure Capacitor bridge is ready
const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        staleTime: Infinity,
        retry: 1,
        onError: error => {
          // Global error handler to prevent query errors from crashing the app
          logger.error({
            message: 'Query error',
            data: {},
            error: error instanceof Error ? error : new Error(String(error)),
          });
        },
      },
    },
  },
};
const app = createApp(App)
  .directive('disable-swipe-back', DisableSwipeBackDirective)
  .use(IonicVue, {
    mode: 'ios',
    rippleEffect: false,
    animated: false,
    // navAnimation: () => {
    //   return {
    //     duration: 200,
    //     curve: 'easeInOut',
    //     direction: 'forward',
    //     property: 'transform',
    //     from: 'translateX(100%)',
    //     to: 'translateX(0%)',
    //     reverse: {
    //       from: 'translateX(0%)',
    //       to: 'translateX(-100%)',
    //     },
    //   };
    // },
  })
  .use(IonicCmpPlugin)
  .use(BaseCmpPlugin)
  .use(router)
  .use(i18n)
  .use(VueQueryPlugin, vueQueryPluginOptions);

// Global error handlers
window.onerror = (message, source, lineno, colno, error) => {
  const errorMessage = error?.message || String(message);
  const stack = error?.stack || `at ${source}:${lineno}:${colno}`;
  console.error('Global error:', errorMessage, stack);
  // Static message: the raw error text travels in `error`, where it is classified.
  logger.error({
    message: 'Unhandled error',
    data: { source, lineno, colno },
    error: error || new Error(errorMessage),
  });
  return false; // Don't prevent default error handling
};

window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  const errorMessage = error.message || String(event.reason);
  console.error('Unhandled promise rejection:', errorMessage, error.stack);
  logger.error({
    message: 'Unhandled promise rejection',
    data: {},
    error,
  });
  event.preventDefault(); // Prevent default browser console error
};

router
  .isReady()
  .then(async () => {
    app.mount('#app');
    // Leave the splash failsafe running — App.vue releases splash after the Live Update gate.
    await logAppOpen();
    void initMetaAppEvents();
  })
  .catch(error => {
    console.error('Router initialization failed:', error);
    logger.error({
      message: 'Router initialization failed',
      data: {},
      error: error instanceof Error ? error : new Error(String(error)),
    });
    // Still mount the app to prevent white screen
    app.mount('#app');
    clearSplashFailsafe();
    SplashScreen.hide().catch(() => undefined);
  });
